import { Dispatch } from 'redux';
import {
    setCalendarType,
    setCompactCalendarData,
    setScrollCalendarData,
} from '../actions/infiniteCalendarSeries';
import { TlzEnvironment } from '../api/constants';
import { getEventCalendarDates } from '../api/eventDates';
import {
    mapEventDatesToCompactCalendarData,
    mapEventDatesToScrollCalendarData,
} from '../components/TimedEntrySeries/CalendarContainer/utils';
import type { MonthAndYear } from '../components/TimedEntrySeries/types';
import { getAppLocale } from '../selectors/app';
import { getBffRequestBaseParams } from '../selectors/bff';
import {
    getParentEventInfo,
    getScrollCalendarData,
    getTimezone,
} from '../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import { CheckoutState } from '../state';
import {
    CalendarType,
    DateApiResponse,
    type ScrollCalendarData,
} from '../state/infiniteCalendarSeries';

const shouldSkipLoading = (
    previousCalendarData: ScrollCalendarData,
    monthAndYear: MonthAndYear,
): boolean => {
    return Boolean(
        previousCalendarData?.years?.[monthAndYear.year]?.months?.[
            monthAndYear.month
        ],
    );
};

export const _initializeScrollCalendarData = (
    previousCalendarData: ScrollCalendarData,
    monthAndYear: MonthAndYear,
): ScrollCalendarData => {
    const calendarData = previousCalendarData || { years: {} };
    calendarData.years = calendarData.years || {};
    calendarData.years[monthAndYear.year] = calendarData.years[
        monthAndYear.year
    ] || {
        months: {},
    };
    calendarData.years[monthAndYear.year].months[monthAndYear.month] =
        calendarData.years[monthAndYear.year].months[monthAndYear.month] || {
            isLoading: true,
            dates: {},
        };

    return calendarData;
};

const updateScrollCalendar = (
    response: DateApiResponse,
    monthAndYear: MonthAndYear,
    state: CheckoutState,
    dispatch: Dispatch<CheckoutState>,
) => {
    const previousCalendarData = getScrollCalendarData(state);
    const timezone = getTimezone(state);
    const scrollCalendarData = mapEventDatesToScrollCalendarData(
        response.eventDates,
        previousCalendarData,
        monthAndYear,
        timezone,
    );
    dispatch(setScrollCalendarData(scrollCalendarData));
};

const updateCompactCalendar = (
    response: DateApiResponse,
    state: CheckoutState,
    dispatch: Dispatch<CheckoutState>,
) => {
    const locale = getAppLocale(state).replace('_', '-');
    const timezone = getTimezone(state);
    const compactCalendarData = mapEventDatesToCompactCalendarData(
        response.eventDates,
        timezone,
        locale,
    );
    dispatch(setCompactCalendarData(compactCalendarData));
};

export const _fetchAndUpdateCalendarData = async (
    dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
    monthAndYear: MonthAndYear,
    eventId: string,
    env: TlzEnvironment,
): Promise<void> => {
    try {
        const params = {
            eventId,
            env,
            month: monthAndYear.month + 1,
            year: monthAndYear.year,
        };
        const state = getState();
        const response = await getEventCalendarDates(params);
        if (response.calendarType === CalendarType.scrollCalendar) {
            updateScrollCalendar(response, monthAndYear, state, dispatch);
        } else {
            updateCompactCalendar(response, state, dispatch);
        }
        dispatch(setCalendarType(response.calendarType));
    } catch (error) {
        console.error('Error fetching calendar data:', error);
    }
};

export const loadDatesForMonth =
    (monthAndYear: MonthAndYear) =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const state = getState();
        const previousScrollCalendarData = getScrollCalendarData(state) || {};
        const { id: eventId } = getParentEventInfo(state);
        const { env } = getBffRequestBaseParams(state);

        if (shouldSkipLoading(previousScrollCalendarData, monthAndYear)) {
            return;
        }

        const calendarData = _initializeScrollCalendarData(
            previousScrollCalendarData,
            monthAndYear,
        );
        dispatch(setScrollCalendarData(calendarData));

        await _fetchAndUpdateCalendarData(
            dispatch,
            getState,
            monthAndYear,
            eventId,
            env,
        );
    };
